import {
  type JSX,
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useRef,
} from "react";

import { useTreeItemUtils } from "@mui/x-tree-view/hooks";
import { ConfigurationTreeItemTypeUnion } from "@plansoft-configuration-renderer/components";
import { useCsConfiguration } from "@plansoft-configuration-renderer/framework";

import { isTreeExpandedByDefault } from "@/tools/helper";

import { TreeItemWithSuspense } from "./TreeItemWithSuspense";

export type ConfigurationTreeItemUiProps = ConfigurationTreeItemTypeUnion & {
  label: ReactNode;
  children?: ReactNode;
  itemId: string;
};

/**
 * Depth of a tree item, provided to its descendants. Top-level items render
 * without an ancestor provider and therefore read the default `0`. This is
 * computed during render, so (unlike the MUI item registration) it is reliable
 * before the lazily-loaded `TreeItem` has registered itself.
 */
const TreeItemDepthContext = createContext(0);

export function ConfigurationTreeItemUi(
  props: ConfigurationTreeItemUiProps,
): JSX.Element {
  const { itemId, label, children } = props;
  const { properties } = useCsConfiguration();
  const { publicAPI, status } = useTreeItemUtils({ itemId, children });
  const depth = useContext(TreeItemDepthContext);
  const attemptsRef = useRef(0);

  const expandByDefault = isTreeExpandedByDefault(properties);
  const isTopLevel = depth === 0;

  useEffect(() => {
    if (!expandByDefault || !isTopLevel || !status.expandable) {
      return;
    }

    // The package keeps `expandedItems` in controlled React state and replaces
    // the whole list on every toggle. When several top-level nodes expand in
    // the same commit they all read the same stale list, so the last write
    // wins and the others are lost. Retrying once per frame self-heals this:
    // each frame the still-collapsed node re-applies its expansion against the
    // now-current list, until it sticks. (No `flushSync` - the lazy sub-config
    // Suspense boundaries would throw on synchronous input.)
    let rafId = 0;
    let cancelled = false;
    attemptsRef.current = 0;

    const tryExpand = (): void => {
      if (cancelled || publicAPI.isItemExpanded(itemId)) {
        return;
      }
      if (attemptsRef.current > 50) {
        return;
      }
      attemptsRef.current += 1;
      publicAPI.setItemExpansion({ itemId, shouldBeExpanded: true });
      rafId = requestAnimationFrame(tryExpand);
    };
    tryExpand();

    return () => {
      cancelled = true;
      cancelAnimationFrame(rafId);
    };
  }, [itemId, expandByDefault, isTopLevel, status.expandable, publicAPI]);

  return (
    <TreeItemWithSuspense itemId={itemId} label={label}>
      <TreeItemDepthContext.Provider value={depth + 1}>
        {children}
      </TreeItemDepthContext.Provider>
    </TreeItemWithSuspense>
  );
}
